import { useField } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { isNil } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { Margin } from '../../layout'
import { Span } from '../../typography/Body'
import { Label } from '../label'
import {
  StCharacterCounter,
  StFieldContainer,
  StInputContainer,
  StInputIcon,
  stInputErrorContainer,
} from './TextArea.styled'

import type { ChangeEvent, FormEvent, TextareaHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type InputProps = {
  label?: string
  noMargin?: boolean
  autoGrow?: boolean
  maxHeight?: number
} & PartiallyRequired<TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'>

const AnimatedIcon = motion(StInputIcon)
const AnimatedFieldContainer = motion(StFieldContainer)
const AnimatedFieldErrorContainer = motion(stInputErrorContainer)

const FormikTextArea = ({ label, noMargin, maxLength, autoGrow, maxHeight, style, ...props }: InputProps) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [field, meta, helpers] = useField(props)
  const [charCount, setCharCount] = useState(field?.value?.length || 0)

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasError = useMemo(() => meta.touched && !isNil(meta.error), [meta.error, meta.touched])

  const animatedErrorMessageProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [])

  useEffect(() => {
    if (autoGrow && inputRef.current) {
      resizeTextarea(inputRef.current)
    }
  }, [autoGrow, resizeTextarea])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(event.target.value.length)
    helpers.setValue(event.target.value)
  }

  const handleAutoResize = (event: FormEvent<HTMLTextAreaElement>) => {
    if (autoGrow) {
      resizeTextarea(event.currentTarget)
    }
  }

  return (
    <AnimatedFieldContainer noMargin={noMargin} showMaxLength={!!maxLength} $autoGrow={autoGrow}>
      {label ? (
        <Label
          htmlFor={props.name}
          color={theme.Colors.Neutrals.Darker}
          style={{ marginBottom: theme.UI.SpacingPx.Space2 }}
        >
          {label} {props.required ? '*' : null}
        </Label>
      ) : null}
      <StInputContainer error={hasError} $autoGrow={autoGrow} $maxHeight={maxHeight}>
        <textarea
          {...field}
          {...props}
          ref={inputRef}
          onChange={handleInputChange}
          onInput={handleAutoResize}
          maxLength={maxLength}
          style={{ ...style }}
        />
        {maxLength ? (
          <StCharacterCounter>
            {charCount}/{maxLength}
          </StCharacterCounter>
        ) : null}

        <AnimatePresence mode='wait' initial={false}>
          {hasError ? (
            <AnimatedIcon
              key='error-icon'
              icon={['far', 'triangle-exclamation']}
              {...getDropInProps({
                hidden: { opacity: 0, x: 30 },
                visible: { opacity: 1, x: 0 },
                exit: { opacity: 0, x: 30 },
                transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
              })}
            />
          ) : null}
        </AnimatePresence>
      </StInputContainer>

      <AnimatePresence mode='wait'>
        {hasError && meta.error ? (
          <AnimatedFieldErrorContainer key={meta.error} {...animatedErrorMessageProps}>
            <Margin mt={theme.UI.Spacing.Space2} />
            <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
              {meta.error}
            </Span>
          </AnimatedFieldErrorContainer>
        ) : null}
      </AnimatePresence>
    </AnimatedFieldContainer>
  )
}

export { FormikTextArea }
